import React from "react"
import styled from "styled-components"

/**
 * Notched floating-label input (parking design language).
 *
 * The label sits in the top-left border notch: absolutely positioned at the
 * top edge, shifted up by half its height, and given the surface background
 * so it punches cleanly through the input's top border. Mirrors the mockup
 * `.checkout-form .input` + `NotchedField` from the parking redesign.
 *
 * Standard controlled-input contract: value, onChange, onBlur, error,
 * placeholder, maxLength, type, name, id, etc. `error` (truthy) flips the
 * label + border to the error color.
 *
 * Pass `children` to mount a non-<input> control (Google Place Autocomplete
 * widget, react-select) inside the same notched border shell — the control
 * should render borderless so only this shell's border shows.
 *
 * Leading-icon support is intentionally omitted in this step — the parking
 * Material Symbols font isn't loaded yet, so a glyph would render blank.
 */
const Wrap = styled.div`
  position: relative;
  width: 100%;
  /* A global rule floats every <input> left (style/GlobalStyle/mobile.js).
   * A floated input contributes no height, so without a Block Formatting
   * Context this wrapper collapsed to 0 and stacked fields overlapped.
   * display: flow-root contains the float so the wrapper keeps the input's
   * height. */
  display: flow-root;
`

const Label = styled.label`
  position: absolute;
  top: 0;
  left: 12px;
  transform: translateY(-50%);
  background: var(--notch-surface, ${(p) => p.theme.colors.background.main});
  padding: 0 6px;
  font-family: ${(p) => p.theme.parking.fonts.grotesk};
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${(p) =>
    p.error ? p.theme.parking.colors.error : p.theme.parking.colors.primary};
  z-index: 2;
  pointer-events: none;
  line-height: 1;
  white-space: nowrap;
`

const Input = styled.input`
  width: 100%;
  height: 48px;
  background: transparent;
  border: 1px solid
    ${(p) =>
      p.error ? p.theme.parking.colors.error : "rgba(114, 118, 135, 0.3)"};
  border-radius: ${(p) => p.theme.parking.radius.lg};
  padding: 0 14px;
  box-sizing: border-box;
  font-family: ${(p) => p.theme.parking.fonts.grotesk};
  font-size: 16px;
  color: ${(p) => p.theme.parking.colors.onSurface};
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  &::placeholder {
    color: rgba(114, 118, 135, 0.5);
  }
  &:focus {
    border-color: ${(p) =>
      p.error ? p.theme.parking.colors.error : p.theme.parking.colors.primary};
    box-shadow: 0 0 0 1px
      ${(p) =>
        p.error ? p.theme.parking.colors.error : p.theme.parking.colors.primary};
  }
`

// Borderless shell for non-<input> controls dropped into the notch (Google
// Place Autocomplete widget, react-select). Same border/radius/height as
// Input; focus-within drives the ring since the focusable element is a child.
const Box = styled.div`
  width: 100%;
  height: 48px;
  box-sizing: border-box;
  background: transparent;
  border: 1px solid
    ${(p) =>
      p.error ? p.theme.parking.colors.error : "rgba(114, 118, 135, 0.3)"};
  border-radius: ${(p) => p.theme.parking.radius.lg};
  transition: border-color 0.15s, box-shadow 0.15s;
  &:focus-within {
    border-color: ${(p) =>
      p.error ? p.theme.parking.colors.error : p.theme.parking.colors.primary};
    box-shadow: 0 0 0 1px
      ${(p) =>
        p.error ? p.theme.parking.colors.error : p.theme.parking.colors.primary};
  }
`

const NotchedField = ({ label, error, id, inputId, children, ...rest }) => (
  <Wrap>
    {label ? (
      <Label htmlFor={inputId || id} error={error}>
        {label}
      </Label>
    ) : null}
    {children ? (
      <Box error={error}>{children}</Box>
    ) : (
      <Input id={inputId || id} error={error} {...rest} />
    )}
  </Wrap>
)

export default NotchedField
